import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { t, lang, cart, updateQty, removeItem, cartTotal, cartKeyOf } = useApp();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div data-testid="cart-empty" className="bg-[#fbf8f1] min-h-[70vh] grid place-items-center px-4">
        <div className="text-center">
          <ShoppingBag size={48} className="mx-auto text-stone-400" />
          <h2 className="mt-5 text-2xl font-semibold text-stone-900">{t.common.empty}</h2>
          <Link
            data-testid="cart-browse-link"
            to="/products"
            className="mt-6 inline-block bg-[#274e13] hover:bg-[#38761d] text-white px-6 py-3 rounded-sm font-semibold text-sm transition-colors"
          >
            {t.common.browseProducts}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="bg-[#fbf8f1] min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h1 className={`text-4xl tracking-tighter font-bold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
          {t.cart.title}
        </h1>

        <div className="mt-10 bg-white border border-stone-200 rounded-sm">
          {cart.map((it) => {
            const key = cartKeyOf(it);
            const subtotal = it.weight_kg * it.price_per_kg * it.quantity;
            return (
              <div
                key={key}
                data-testid={`cart-item-${key}`}
                className="flex items-center gap-4 p-4 border-b border-stone-100 last:border-0"
              >
                <img src={resolveImageUrl(it.image_url)} alt="" className="w-20 h-20 object-cover rounded-sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 truncate">
                    {lang === "en" ? it.name_en : it.name_te}
                  </div>
                  <div className="text-xs text-stone-500">
                    {it.weight_kg}kg × ₹{it.price_per_kg.toFixed(0)} / {t.common.kg}
                  </div>
                </div>
                <div className="flex items-center border border-stone-300 rounded-sm">
                  <button
                    data-testid={`cart-dec-${key}`}
                    onClick={() => updateQty(key, it.quantity - 1)}
                    className="p-1.5 hover:bg-stone-100"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="px-3 text-sm font-semibold">{it.quantity}</span>
                  <button
                    data-testid={`cart-inc-${key}`}
                    onClick={() => updateQty(key, it.quantity + 1)}
                    className="p-1.5 hover:bg-stone-100"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <div className="w-24 text-right font-semibold text-stone-900">
                  ₹{subtotal.toFixed(0)}
                </div>
                <button
                  data-testid={`cart-remove-${key}`}
                  onClick={() => removeItem(key)}
                  className="p-2 text-stone-400 hover:text-red-600"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-[#f2ebd9] border border-stone-200 rounded-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-stone-500 font-semibold">
              {t.common.total}
            </div>
            <div className="text-3xl font-bold text-stone-900 mt-1" data-testid="cart-total">
              ₹{cartTotal.toFixed(0)}
            </div>
          </div>
          <button
            data-testid="proceed-checkout-btn"
            onClick={() => navigate("/checkout")}
            className="bg-[#274e13] hover:bg-[#38761d] text-white px-8 py-3.5 rounded-sm font-semibold text-sm tracking-wide transition-colors"
          >
            {t.cart.proceed}
          </button>
        </div>
      </div>
    </div>
  );
}
